// ─── settings drawer ─────────────────────────────────────────────────────────
// Every knob the three seams expose: which provider (and where it runs), what the
// persona is, which voice, how turns are taken, which face — plus the file picker
// for a face that takes a user-supplied model.
//
// Two classes of change, kept honest: face and turn-taking apply live, while
// anything baked into `POST /v1/sessions` needs a new session, so the Apply button
// says so instead of silently doing nothing.

import type { Placement, TurnTaking } from '@shared/protocol.ts'
import { FACE_IDS } from '@voicebox/face'
import type { Settings } from '../prefs.ts'
import { audioLabel, describe as describeProvider, usable, type ProviderInfo } from '../providers.ts'

export type { ProviderInfo }

export interface SettingsHandlers {
  /** Applied and persisted immediately. */
  onChange(patch: Partial<Settings>): void
  /** Re-open the session with the current settings. */
  onApply(): void
  /** One click: choose a provider and restart the session on it. */
  onPickProvider(id: string): void
  onPickModel(file: File): void
  onClearModel(): void
}

export class SettingsPanel {
  private readonly panel: HTMLElement
  private readonly scrim: HTMLElement
  private readonly form: HTMLElement
  private readonly apply: HTMLButtonElement
  private readonly model: HTMLElement
  private providers: ProviderInfo[] = []
  private settings: Settings
  private dirty = false

  constructor(
    host: HTMLElement,
    settings: Settings,
    private readonly handlers: SettingsHandlers,
  ) {
    this.settings = settings
    // The drawer sits over the app, so what is behind it is dimmed and inert —
    // otherwise the composer underneath is half-visible and half-clickable.
    this.scrim = document.createElement('div')
    this.scrim.id = 'scrim'
    this.scrim.hidden = true
    this.scrim.onclick = () => this.close()
    host.append(this.scrim)

    this.panel = document.createElement('aside')
    this.panel.id = 'settings'
    this.panel.className = 'glass'
    this.panel.dataset.open = 'false'
    this.panel.innerHTML = `
      <div class="row">
        <h2 class="grow">Settings</h2>
        <button class="icon-btn" id="settings-close" aria-label="Close settings">✕</button>
      </div>
      <div id="settings-form" style="display:grid;gap:14px"></div>
      <div class="sep"></div>
      <div id="model-slot" style="display:grid;gap:8px"></div>
      <div class="sep"></div>
      <button class="ghost" id="apply">Apply</button>
      <div class="note" style="color:var(--dim);font-size:11px">
        Provider, persona, voice and placement are fixed for the life of a session — applying them opens a new one.
      </div>`
    host.append(this.panel)
    this.form = this.panel.querySelector('#settings-form')!
    this.model = this.panel.querySelector('#model-slot')!
    this.apply = this.panel.querySelector('#apply')!
    this.panel.querySelector<HTMLElement>('#settings-close')!.onclick = () => this.close()
    this.apply.onclick = () => {
      this.dirty = false
      this.renderApply()
      handlers.onApply()
      this.close()
    }
    this.render()
  }

  get open(): boolean {
    return this.panel.dataset.open === 'true'
  }

  toggle(): void {
    this.setOpen(!this.open)
  }

  /** Open the drawer with the avatar picker in view — the "bring your own .vrm" path. */
  openAt(section: 'model'): void {
    this.setOpen(true)
    if (section === 'model') this.model.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  close(): void {
    this.setOpen(false)
  }

  private setOpen(open: boolean): void {
    this.panel.dataset.open = String(open)
    this.scrim.hidden = !open
  }

  setProviders(providers: ProviderInfo[]): void {
    this.providers = providers
    this.render()
  }

  setValues(settings: Settings): void {
    this.settings = settings
    this.render()
  }

  /** The mounted face decides whether a model picker makes sense at all. */
  setModelState(state: { takesModel: boolean; hasModel: boolean; name: string }): void {
    this.model.replaceChildren()
    if (!state.takesModel) {
      this.model.append(
        note(`The ${this.settings.face} face draws itself — no model needed. The vrm face takes a .vrm you supply.`),
      )
      return
    }
    const picker = document.createElement('input')
    picker.type = 'file'
    picker.accept = '.vrm,.mavatar,.json,model/gltf-binary,application/octet-stream'
    picker.onchange = () => {
      const file = picker.files?.[0]
      if (file) this.handlers.onPickModel(file)
    }
    const label = document.createElement('label')
    label.textContent = this.settings.face === 'mesh' ? 'Your .mavatar avatar' : this.settings.face === 'layered' ? 'Your .layered.json avatar' : 'Your .vrm avatar'
    const clear = document.createElement('button')
    clear.className = 'ghost'
    clear.textContent = 'Remove'
    clear.onclick = () => this.handlers.onClearModel()

    const pickerRow = row(picker)
    if (state.hasModel) pickerRow.append(clear)

    this.model.append(
      label,
      pickerRow,
      note(
        state.hasModel
          ? `Loaded: ${state.name || 'your model'} — kept in this browser only.`
          : 'No avatar bundled, ever: VRM licences are not ours to redistribute. Export one from VRoid Studio and pick it here.',
      ),
    )
  }

  private render(): void {
    const s = this.settings
    const provider = this.providers.find((p) => p.id === s.provider)
    const placements: Array<{ value: string; label: string }> = [
      { value: '', label: `provider default${provider ? ` (${provider.placements[0]})` : ''}` },
      ...(provider?.placements ?? (['server', 'client'] as Placement[])).map((p) => ({
        value: p,
        label: p === 'client' ? 'client — audio stays on this machine' : 'server — the session holds the model',
      })),
    ]

    this.form.replaceChildren(
      this.providerList(),
      select('Placement', s.placement, placements, (v) => this.patch({ placement: v as Settings['placement'] }, true)),
      textarea('Persona / system prompt', s.persona, (v) => this.patch({ persona: v }, true)),
      text('Greeting', s.greeting, 'spoken as soon as the session connects', (v) => this.patch({ greeting: v }, true)),
      text('Voice', s.voice, 'passed through to the provider', (v) => this.patch({ voice: v }, true)),
      select(
        'Turn taking',
        s.turnTaking,
        [
          { value: 'vad', label: 'voice — it decides when you stopped' },
          { value: 'ptt', label: 'push — hold space to talk' },
        ],
        (v) => this.patch({ turnTaking: v as TurnTaking }),
      ),
      select(
        'Local endpoint',
        s.localBaseUrl,
        [
          { value: 'http://localhost:1234/v1', label: 'http://localhost:1234/v1 — LM Studio' },
          { value: 'http://127.0.0.1:8080/v1', label: 'http://127.0.0.1:8080/v1 — llama.cpp / whisper.cpp' },
          { value: 'http://localhost:11434/v1', label: 'http://localhost:11434/v1 — Ollama' },
        ],
        (v) => this.patch({ localBaseUrl: v }, true),
        true,
      ),
      note('used when a client-placed provider runs the model in this browser'),
      select(
        'Face',
        s.face,
        FACE_IDS.map((id) => ({ value: id, label: id })),
        (v) => this.patch({ face: v }),
      ),
    )
    this.renderApply()
  }

  /**
   * The provider picker: the service's own catalog, one click per switch. Providers
   * that cannot be used are shown dimmed with the reason rather than hidden — a
   * missing key must be visible, not a mystery.
   */
  private providerList(): HTMLElement {
    const wrap = field('Provider')
    const list = document.createElement('div')
    list.className = 'providers'

    const known = this.providers.length
      ? this.providers
      : [{ id: this.settings.provider, placements: ['server' as Placement], status: 'ready' as const }]

    for (const p of known) {
      const ready = usable(p)
      const card = document.createElement('button')
      card.className = 'provider'
      card.dataset.id = p.id
      card.dataset.usable = String(ready)
      card.setAttribute('aria-pressed', String(p.id === this.settings.provider))
      // Listed, dimmed and explained — never hidden, and never quietly selected: a
      // provider whose key is missing must say so instead of failing at the mic.
      if (!ready) card.setAttribute('aria-disabled', 'true')

      const meta = [ready ? p.placements.join('/') : p.status === 'planned' ? 'not implemented' : 'not configured', audioLabel(p)]
      card.append(
        row(
          Object.assign(document.createElement('b'), { textContent: p.id }),
          Object.assign(document.createElement('span'), { className: 'tag', textContent: meta.filter(Boolean).join(' · ') }),
        ),
        Object.assign(document.createElement('span'), { className: 'why', textContent: describeProvider(p) }),
      )
      card.onclick = () => this.handlers.onPickProvider(p.id)
      list.append(card)
    }

    wrap.append(
      list,
      note(
        this.providers.length
          ? 'one click switches provider and restarts the session'
          : 'the service did not answer /v1/providers — this is the saved choice',
      ),
    )
    return wrap
  }

  private patch(patch: Partial<Settings>, needsSession = false): void {
    this.settings = { ...this.settings, ...patch }
    this.dirty ||= needsSession
    this.handlers.onChange(patch)
    this.render()
  }

  private renderApply(): void {
    this.apply.textContent = this.dirty ? 'Apply — restarts the session' : 'Reconnect'
    this.apply.id = 'apply'
    this.apply.style.borderColor = this.dirty ? 'rgba(94,231,255,.45)' : ''
    this.apply.style.background = this.dirty ? 'rgba(94,231,255,.18)' : ''
  }
}

// ── field builders ───────────────────────────────────────────────────────────
function field(label: string): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'field'
  wrap.append(Object.assign(document.createElement('label'), { textContent: label }))
  return wrap
}

function row(...children: HTMLElement[]): HTMLElement {
  const node = document.createElement('div')
  node.className = 'row'
  node.append(...children)
  return node
}

function note(text: string): HTMLElement {
  return Object.assign(document.createElement('div'), { className: 'note', textContent: text })
}

function select(
  label: string,
  value: string,
  options: Array<{ value: string; label: string }>,
  onChange: (value: string) => void,
  editable = false,
): HTMLElement {
  const wrap = field(label)
  if (editable) {
    // A datalist keeps the presets discoverable without pretending the list is closed.
    const input = document.createElement('input')
    const list = document.createElement('datalist')
    list.id = `dl-${label.replace(/\W+/g, '-')}`
    for (const o of options) list.append(Object.assign(document.createElement('option'), { value: o.value }))
    input.setAttribute('list', list.id)
    input.value = value
    input.onchange = () => onChange(input.value.trim())
    wrap.append(input, list)
    return wrap
  }
  const node = document.createElement('select')
  const known = options.some((o) => o.value === value)
  for (const o of known ? options : [{ value, label: value }, ...options]) {
    node.append(Object.assign(document.createElement('option'), { value: o.value, textContent: o.label }))
  }
  node.value = value
  node.onchange = () => onChange(node.value)
  wrap.append(node)
  return wrap
}

function text(label: string, value: string, hint: string, onChange: (value: string) => void): HTMLElement {
  const wrap = field(label)
  const input = document.createElement('input')
  input.value = value
  input.placeholder = hint
  input.onchange = () => onChange(input.value)
  wrap.append(input, note(hint))
  return wrap
}

function textarea(label: string, value: string, onChange: (value: string) => void): HTMLElement {
  const wrap = field(label)
  const node = document.createElement('textarea')
  node.value = value
  node.onchange = () => onChange(node.value)
  wrap.append(node)
  return wrap
}
